/**
 * Hook para buscar KPIs consolidados do fluxo via RPC server-side
 * Agrega dados de flow_executions + flow_chat_sessions numa única chamada
 * 
 * @release v1.0.0 - 2026-03-08
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger condicional para debug
const logger = createLogger('FlowResultsKpi');

/** Filtros aplicáveis aos KPIs */
export interface FlowResultsKpiFilters {
  startDate?: string | null;
  endDate?: string | null;
  inboxId?: string | null;
  triggerType?: string | null;
  status?: string | null;
  search?: string | null;
}

/** Estrutura dos KPIs retornados pela RPC */
export interface FlowResultsKpiData {
  total_sessions: number;
  completed_count: number;
  completion_rate: number;
  avg_duration_seconds: number | null;
  views: number;
}

/**
 * Hook para buscar KPIs consolidados do fluxo
 * 
 * @param flowId - ID do fluxo
 * @param filters - Filtros de período, inbox, trigger, status, busca
 */
export function useFlowResultsKpi(
  flowId: string | undefined,
  filters: FlowResultsKpiFilters = {}
) {
  const { currentTenant } = useAuth();

  const query = useQuery({
    queryKey: ['flow-results-kpi', flowId, currentTenant?.id, filters],
    queryFn: async () => {
      logger.debug('Buscando KPIs do fluxo:', { flowId, filters });

      // Chama RPC consolidada
      const { data, error } = await (supabase.rpc as any)(
        'get_flow_results_kpi',
        {
          p_flow_id: flowId,
          p_start_date: filters.startDate || null,
          p_end_date: filters.endDate || null,
          p_inbox_id: filters.inboxId || null,
          p_trigger_type: filters.triggerType || null,
          p_status: filters.status || null,
          p_search: filters.search || null,
          p_tenant_hint: currentTenant?.id || null,
        }
      );

      if (error) {
        logger.error('Erro ao buscar KPIs do fluxo:', error);
        throw error;
      }

      // RPC retorna array com 1 registro
      const row = Array.isArray(data) ? data[0] : data;

      const result: FlowResultsKpiData = {
        total_sessions: Number(row?.total_sessions ?? 0),
        completed_count: Number(row?.completed_count ?? 0),
        completion_rate: Number(row?.completion_rate ?? 0),
        avg_duration_seconds: row?.avg_duration_seconds ? Number(row.avg_duration_seconds) : null,
        views: Number(row?.views ?? 0),
      };

      logger.debug('KPIs recebidos:', result);
      return result;
    },
    enabled: !!flowId && !!currentTenant?.id,
    staleTime: 30 * 1000, // 30 segundos
    gcTime: 5 * 60 * 1000, // 5 minutos
  });

  return {
    kpi: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
}
